<template>
  <Modal
    v-model="visible"
    :title="modalTitle"
    :mask-closable="false"
    width="760"
    @on-cancel="handleCancel"
  >
    <Form v-if="visible" ref="formRef" :model="formData" :label-width="110">
      <FormItem
        label="活动类型"
        prop="activityType"
        :rules="[
          {
            required: true,
            type: 'number',
            message: '请选择活动类型',
            trigger: 'change'
          }
        ]"
      >
        <Select
          v-model="formData.activityType"
          placeholder="请选择活动类型"
          :disabled="!!formData.id || readonly"
          @on-change="handleActivityTypeChange"
        >
          <Option
            v-for="item in activityTypeOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </Option>
        </Select>
      </FormItem>

      <template v-if="requiresModules">
        <div
          v-for="(module, index) in formData.moduleConfigs"
          :key="module.moduleKey"
          class="module-card"
        >
          <FormItem :label="`模块${index + 1}`" class="module-card__form-item">
            <div class="module-card__inline">
              <div class="module-card__field">
                <Input
                  v-model.trim="module.moduleName"
                  :disabled="readonly"
                  placeholder="请输入模块标题"
                />
              </div>
              <Icon
                v-if="!readonly && formData.moduleConfigs.length > 1"
                type="ios-close"
                class="module-card__remove"
                @click="removeModule(index)"
              />
            </div>
          </FormItem>

          <FormItem label="内容类型" class="module-card__form-item">
            <div class="module-card__inline">
              <div class="module-card__field">
                <Select
                  v-model="module.contentType"
                  placeholder="请选择内容类型"
                  :disabled="readonly"
                  @on-change="handleModuleContentTypeChange(index, $event)"
                >
                  <Option
                    v-for="item in moduleContentTypeOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </Option>
                </Select>
              </div>
              <span class="module-card__toggle" @click="toggleModule(index)">
                {{ module.expanded ? '收起' : '展开' }}
                <Icon
                  type="ios-arrow-down"
                  class="module-card__arrow"
                  :class="{ 'module-card__arrow--expanded': module.expanded }"
                />
              </span>
            </div>
          </FormItem>

          <div v-show="module.expanded" class="module-card__body">
            <template v-if="Number(module.contentType) === 1">
              <FormItem label="iOS付费项">
                <Select
                  v-model="module.iosGoodsIds"
                  multiple
                  filterable
                  clearable
                  :disabled="readonly"
                  :loading="goodsLoading"
                  placeholder="请选择iOS付费项"
                >
                  <Option
                    v-for="item in iosGoodsOptions"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ formatGoodsLabel(item) }}
                  </Option>
                </Select>
              </FormItem>

              <FormItem label="安卓付费项">
                <Select
                  v-model="module.androidGoodsIds"
                  multiple
                  filterable
                  clearable
                  :disabled="readonly"
                  :loading="goodsLoading"
                  placeholder="请选择安卓付费项"
                >
                  <Option
                    v-for="item in androidGoodsOptions"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ formatGoodsLabel(item) }}
                  </Option>
                </Select>
              </FormItem>
            </template>

            <template v-if="Number(module.contentType) === 2">
              <FormItem label="剧集数量">
                <InputNumber
                  v-model="module.recommendCount"
                  :min="1"
                  :max="50"
                  :precision="0"
                  style="width: 160px"
                  :disabled="readonly"
                />
              </FormItem>

              <FormItem label="推荐内容">
                <RadioGroup
                  v-model="module.recommendType"
                  type="button"
                  :disabled="readonly"
                  @on-change="handleModuleRecommendTypeChange(index, $event)"
                >
                  <Radio
                    v-for="item in moduleRecommendTypeOptions"
                    :key="item.value"
                    :label="item.value"
                    :disabled="readonly"
                  >
                    {{ item.label }}
                  </Radio>
                </RadioGroup>
              </FormItem>

              <template v-if="Number(module.recommendType) === 1">
                <div class="module-recommend-manual">
                  <div class="module-hint">
                  手动配置的剧集推荐优先级高于系统推荐，未配置语言时将由客户端兜底。
                </div>

                <Button
                  type="primary"
                  ghost
                  size="small"
                  class="module-recommend-manual__button"
                  :disabled="readonly"
                  @click="addLanguageCard(index)"
                >
                  + 添加语言主推剧
                </Button>

                </div>

                <div
                  v-for="(card, cardIndex) in module.languageCards"
                  :key="card.cardKey"
                  class="language-card"
                >
                  <Icon
                    v-if="!readonly"
                    type="ios-close"
                    class="language-card__remove"
                    @click="removeLanguageCard(index, cardIndex)"
                  />
                  <FormItem label="语言" :label-width="80">
                    <Select
                      v-model="card.langId"
                      placeholder="请选择语言"
                      style="width: 70%"
                      :disabled="readonly"
                      @on-change="handleLanguageChange(index, cardIndex, $event)"
                    >
                      <Option
                        v-for="item in languageList"
                        :key="item.id"
                        :value="item.id"
                        :disabled="
                          isLanguageDisabled(
                            module.languageCards,
                            item.id,
                            cardIndex
                          )
                        "
                      >
                        {{ item.name }}
                      </Option>
                    </Select>
                  </FormItem>

                  <FormItem
                    v-for="(drama, dramaIndex) in card.dramas"
                    :key="drama.rowKey"
                    :label="`剧目${dramaIndex + 1}`"
                    :label-width="80"
                  >
                    <div class="drama-row">
                      <Select
                        v-model="drama.id"
                        filterable
                        clearable
                        style="width: 70%"
                        :loading="card.loading"
                        :disabled="readonly || !card.langId"
                        placeholder="请输入剧集名称并搜索"
                        @on-query-change="
                          searchLanguageDramas(index, cardIndex, $event)
                        "
                        @on-change="
                          handleCardDramaChange(
                            index,
                            cardIndex,
                            dramaIndex,
                            $event
                          )
                        "
                      >
                        <Option
                          v-for="item in getCardDramaOptions(card)"
                          :key="item.id"
                          :style="{ zIndex: 1000 }"
                          :value="item.id"
                          :disabled="
                            isCardDramaDisabled(card, item.id, dramaIndex)
                          "
                        >
                          {{ item.name }}
                        </Option>
                      </Select>

                      <Icon
                        v-if="!readonly && dramaIndex > 0"
                        type="ios-close-circle"
                        class="drama-row__remove"
                        @click="removeDramaFromCard(index, cardIndex, dramaIndex)"
                      />
                    </div>
                  </FormItem>

                  <span
                    v-if="!readonly"
                    class="language-card__append"
                    @click="addDramaToCard(index, cardIndex)"
                  >
                    + 添加剧目
                  </span>
                </div>
              </template>

              <template v-else>
                <FormItem
                  v-for="(_, dramaIndex) in module.recommendCount"
                  :key="dramaIndex"
                  :label="`剧目${dramaIndex + 1}`"
                >
                  <Select
                    v-model="module.parentPlayIds[dramaIndex]"
                    filterable
                    clearable
                    :loading="specifiedDramaLoading"
                    :disabled="readonly"
                    placeholder="请输入剧集名称并搜索"
                    @on-query-change="searchSpecifiedDramas"
                    @on-change="handleSpecifiedDramaChange(index, dramaIndex, $event)"
                  >
                    <Option
                      v-for="item in getSpecifiedDramaOptions(module, dramaIndex)"
                      :key="item.id"
                      :value="item.id"
                      :disabled="isDramaSelected(module, dramaIndex, item.id)"
                    >
                      {{ item.name }}
                    </Option>
                  </Select>
                </FormItem>
              </template>
            </template>
          </div>
        </div>

        <div class="module-toolbar module-toolbar--bottom">
          <Button
            type="primary"
            icon="md-add"
            size="small"
            :disabled="readonly"
            @click="addModule"
          >
            新增模块
          </Button>
        </div>
      </template>

      <FormItem
        label="H5背景图"
        prop="backgroundFileUrl"
        :rules="[
          {
            required: true,
            message: '请上传H5背景图',
            trigger: 'change'
          }
        ]"
      >
        <div :style="uploadWrapperStyle">
          <PopupUpload
            v-model="formData.backgroundFileUrl"
            width="140"
            height="180"
            :max-size="uploadImageMaxSize"
            title="点击上传 背景图片"
            hint="jpg/png格式，<2mb"
            @on-success="handleUploadSuccess('background', $event)"
            @on-remove="handleUploadRemove('background')"
          />
        </div>
      </FormItem>

      <FormItem
        v-if="isScoreActivity"
        label="H5按钮图"
        prop="buttonFileUrl"
        :rules="[
          {
            required: true,
            message: '请上传H5按钮图',
            trigger: 'change'
          }
        ]"
      >
        <div :style="uploadWrapperStyle">
          <PopupUpload
            v-model="formData.buttonFileUrl"
            width="260"
            height="90"
            :max-size="uploadImageMaxSize"
            title="点击上传 按钮图片"
            hint="jpg/png格式，<2mb"
            @on-success="handleUploadSuccess('button', $event)"
            @on-remove="handleUploadRemove('button')"
          />
        </div>
      </FormItem>

      <FormItem v-if="isScoreActivity" label="弹窗预览">
        <div
          class="score-preview"
          :class="{ 'score-preview--clickable': !!formData.backgroundFileUrl }"
          @click="previewScoreActivity"
        >
          <img
            v-if="formData.backgroundFileUrl"
            :src="formData.backgroundFileUrl"
            class="score-preview__bg"
          />
          <div v-else class="score-preview__placeholder">暂无背景图</div>

          <div v-if="formData.buttonFileUrl" class="score-preview__button">
            <img :src="formData.buttonFileUrl" />
          </div>
        </div>
      </FormItem>

      <FormItem
        v-if="needsHolidayTime"
        label="活动时间"
        prop="activityTimeRange"
        :rules="[
          {
            required: true,
            type: 'array',
            min: 2,
            message: '请选择活动时间',
            trigger: 'change'
          }
        ]"
      >
        <DatePicker
          transfer
          type="datetimerange"
          format="yyyy-MM-dd HH:mm:ss"
          :editable="false"
          :disabled="readonly"
          :value="formData.activityTimeRange"
          placeholder="请选择活动时间"
          style="width: 100%"
          @on-change="handleActivityTimeChange"
        />
      </FormItem>

      <FormItem v-if="showsActivityDesc" label="活动说明">
        <Input
          v-model.trim="formData.activityDesc"
          type="textarea"
          :rows="4"
          :maxlength="200"
          show-word-limit
          :disabled="readonly"
          placeholder="请输入活动说明"
        />
      </FormItem>

      <!-- <FormItem
        label="活动描述"
        prop="activityDesc"
      >
        <Input
          v-model.trim="formData.activityDesc"
          :maxlength="200"
          :disabled="readonly"
          placeholder="请输入活动描述"
        />
      </FormItem> -->

      <FormItem
        label="活动名称"
        prop="activityName"
        :rules="[
          {
            required: true,
            message: '请输入活动名称',
            trigger: 'blur'
          }
        ]"
      >
        <Input
          v-model.trim="formData.activityName"
          :maxlength="50"
          :disabled="readonly"
          placeholder="请输入活动名称（仅后台展示）"
        />
      </FormItem>
    </Form>

    <template #footer>
      <Button @click="handleCancel">{{ readonly ? '关闭' : '取消' }}</Button>
      <Button
        v-if="!readonly"
        type="primary"
        :loading="loading"
        @click="handleSubmit"
      >
        {{ submitButtonText }}
      </Button>
    </template>
  </Modal>
</template>

<script>
import dayjs from 'dayjs'
import { debounce } from 'lodash'
import { InputNumber } from 'view-design'
import PopupUpload from '@/components/Upload/PopupUpload.vue'
import { getGoodsList } from '@/api/goods'
import { addActivityConfig, editActivityConfig } from '@/api/activity'
import { getPrimaryInfo, getPrimaryList } from '@/api/play'
import { getLangList } from '@/api/lang'
import {
  ACTIVITY_TYPE_HOLIDAY,
  ACTIVITY_TYPE_OPTIONS,
  ACTIVITY_TYPE_SCORE,
  ACTIVITY_TYPE_WEEKEND,
  MODULE_CONTENT_TYPE_OPTIONS,
  MODULE_RECOMMEND_TYPE_OPTIONS,
  getActivityTypeLabel
} from '../constants'

const createDramaItem = () => ({
  rowKey: `${Date.now()}_${Math.random()}`,
  id: '',
  name: '',
  createTime: ''
})

const createLanguageCard = () => ({
  cardKey: `${Date.now()}_${Math.random()}`,
  langId: '',
  langName: '',
  dramas: [createDramaItem()],
  dramaOptions: [],
  loading: false
})

const createModule = () => ({
  moduleKey: `${Date.now()}_${Math.random()}`,
  expanded: true,
  moduleName: '',
  contentType: '',
  iosGoodsIds: [],
  androidGoodsIds: [],
  recommendType: 1,
  recommendCount: 10,
  parentPlayIds: [],
  parentPlayNames: [],
  languageCards: []
})

const createFormData = (activityType = ACTIVITY_TYPE_WEEKEND) => ({
  id: '',
  activityType,
  activityName: '',
  activityDesc: '',
  backgroundFileUrl: '',
  backgroundFileId: '',
  buttonFileUrl: '',
  buttonFileId: '',
  bonusCoins: null,
  startTime: '',
  endTime: '',
  activityTimeRange: [],
  moduleConfigs: activityType === ACTIVITY_TYPE_SCORE ? [] : [createModule()]
})

const SCORE_ACTIVITY_IMAGE_MAX_SIZE = 500

export default {
  name: 'ActivityConfigEditModal',
  components: {
    PopupUpload,
    InputNumber
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      goodsLoading: false,
      specifiedDramaLoading: false,
      activityTypeOptions: ACTIVITY_TYPE_OPTIONS,
      moduleContentTypeOptions: MODULE_CONTENT_TYPE_OPTIONS,
      moduleRecommendTypeOptions: MODULE_RECOMMEND_TYPE_OPTIONS,
      formData: createFormData(),
      iosGoodsOptions: [],
      androidGoodsOptions: [],
      languageList: [],
      specifiedDramaOptions: []
    }
  },
  computed: {
    modalTitle() {
      const action = this.readonly ? '预览' : this.formData.id ? '编辑' : '新增'
      return `${action}活动类型-${getActivityTypeLabel(this.formData.activityType)}`
    },
    submitButtonText() {
      return this.formData.id ? '保存' : '新增'
    },
    requiresModules() {
      return (
        this.formData.activityType === ACTIVITY_TYPE_WEEKEND ||
        this.formData.activityType === ACTIVITY_TYPE_HOLIDAY
      )
    },
    needsHolidayTime() {
      return this.formData.activityType === ACTIVITY_TYPE_HOLIDAY
    },
    isScoreActivity() {
      return this.formData.activityType === ACTIVITY_TYPE_SCORE
    },
    showsActivityDesc() {
      return this.requiresModules
    },
    uploadWrapperStyle() {
      return this.readonly
        ? {
            pointerEvents: 'none',
            opacity: 0.75
          }
        : {}
    },
    uploadImageMaxSize() {
      return this.isScoreActivity ? SCORE_ACTIVITY_IMAGE_MAX_SIZE : 2048
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.visible = val
        if (val) {
          if (this.data && this.data.id) {
            this.mapDataToForm(this.data)
          } else {
            this.resetFormData()
          }
          this.initializeOptions()
        }
      }
    },
    visible(val) {
      this.$emit('input', val)
    }
  },
  methods: {
    async initializeOptions() {
      try {
        await Promise.all([this.fetchGoodsOptions(), this.fetchLanguageOptions()])
        this.loadLanguageCardOptions()
        this.loadSpecifiedDramaNames()
      } catch (error) {}
    },
    async fetchGoodsOptions() {
      if (this.isScoreActivity) return
      this.goodsLoading = true
      try {
        const [iosRes, androidRes] = await Promise.all([
          getGoodsList({
            current: -1,
            size: -1,
            goodsVersion: 1,
            goodsChannel: 0
          }),
          getGoodsList({
            current: -1,
            size: -1,
            goodsVersion: 1,
            goodsChannel: 1
          })
        ])
        this.iosGoodsOptions = iosRes.data.records || []
        this.androidGoodsOptions = androidRes.data.records || []
      } catch (error) {
        this.$Message.error('获取付费项失败')
      } finally {
        this.goodsLoading = false
      }
    },
    async fetchLanguageOptions() {
      if (this.isScoreActivity) return
      try {
        const { data } = await getLangList({
          current: 1,
          size: 100,
          langStatus: true
        })
        this.languageList = (data.records || []).map((item) => ({
          id: item.id,
          name: item.chineseLangName
        }))
      } catch (error) {
        this.$Message.error('获取语言列表失败')
      }
    },
    formatGoodsLabel(item) {
      if (!item) return '-'
      if (item.goodsPrice === undefined || item.goodsPrice === null) {
        return item.goodsName
      }
      return `${item.goodsName} (${item.goodsPrice})`
    },
    resetFormData() {
      this.formData = createFormData()
      this.specifiedDramaOptions = []
    },
    handleCancel() {
      this.visible = false
    },
    handleActivityTypeChange(activityType) {
      this.formData = createFormData(activityType)
      this.specifiedDramaOptions = []
      if (!this.isScoreActivity) {
        this.initializeOptions()
      }
    },
    handleActivityTimeChange(value) {
      const range = Array.isArray(value) ? value : []
      this.formData.activityTimeRange = range
      this.formData.startTime = range[0] || ''
      this.formData.endTime = range[1] || ''
    },
    addModule() {
      if (this.readonly) return
      this.formData.moduleConfigs.push(createModule())
    },
    removeModule(index) {
      if (this.readonly) return
      this.formData.moduleConfigs.splice(index, 1)
    },
    toggleModule(index) {
      const module = this.formData.moduleConfigs[index]
      if (!module) return
      this.$set(module, 'expanded', !module.expanded)
    },
    handleModuleContentTypeChange(index, contentType) {
      const current = this.formData.moduleConfigs[index] || createModule()
      const nextModule = {
        ...createModule(),
        moduleKey: current.moduleKey,
        expanded: true,
        moduleName: current.moduleName,
        contentType
      }
      if (Number(contentType) === 2) {
        nextModule.recommendType = 1
        nextModule.recommendCount = 10
      }
      this.$set(this.formData.moduleConfigs, index, nextModule)
    },
    handleModuleRecommendTypeChange(index, recommendType) {
      const module = this.formData.moduleConfigs[index]
      if (!module) return
      this.$set(module, 'recommendType', Number(recommendType || 1))
      this.$set(module, 'parentPlayIds', [])
      this.$set(module, 'parentPlayNames', [])
      this.$set(module, 'languageCards', [])
    },
    handleUploadSuccess(type, data) {
      if (!data) return
      if (type === 'background') {
        this.formData.backgroundFileId = data.fileId
      } else if (type === 'button') {
        this.formData.buttonFileId = data.fileId
      }
    },
    handleUploadRemove(type) {
      if (type === 'background') {
        this.formData.backgroundFileUrl = ''
        this.formData.backgroundFileId = ''
      } else if (type === 'button') {
        this.formData.buttonFileUrl = ''
        this.formData.buttonFileId = ''
      }
    },
    previewScoreActivity() {
      if (!this.formData.backgroundFileUrl) {
        this.$Message.warning('请先上传背景图')
        return
      }
      this.previewCompositeImage(
        this.formData.backgroundFileUrl,
        this.formData.buttonFileUrl
      )
    },
    previewCompositeImage(backgroundUrl, buttonUrl) {
      const backgroundImg = new Image()
      backgroundImg.crossOrigin = 'anonymous'
      backgroundImg.onload = () => {
        if (!buttonUrl) {
          this.$viewerApi({ images: [backgroundUrl] })
          return
        }
        const buttonImg = new Image()
        buttonImg.crossOrigin = 'anonymous'
        buttonImg.onload = () => {
          const gap = 12
          const canvas = document.createElement('canvas')
          canvas.width = backgroundImg.naturalWidth
          canvas.height =
            backgroundImg.naturalHeight + buttonImg.naturalHeight + gap
          const ctx = canvas.getContext('2d')
          ctx.drawImage(
            backgroundImg,
            0,
            0,
            backgroundImg.naturalWidth,
            backgroundImg.naturalHeight
          )
          const buttonX =
            (backgroundImg.naturalWidth - buttonImg.naturalWidth) / 2
          ctx.drawImage(
            buttonImg,
            buttonX,
            backgroundImg.naturalHeight + gap,
            buttonImg.naturalWidth,
            buttonImg.naturalHeight
          )
          this.$viewerApi({ images: [canvas.toDataURL('image/png')] })
        }
        buttonImg.onerror = () => {
          this.$viewerApi({ images: [backgroundUrl] })
        }
        buttonImg.src = buttonUrl
      }
      backgroundImg.onerror = () => {
        this.$Message.error('图片加载失败')
      }
      backgroundImg.src = backgroundUrl
    },
    mapDataToForm(data) {
      const activityType = Number(data.activityType || ACTIVITY_TYPE_WEEKEND)
      const modules = Array.isArray(data.moduleConfigs)
        ? data.moduleConfigs.map((item) => this.normalizeModule(item))
        : []
      this.formData = {
        ...createFormData(activityType),
        id: data.id || '',
        activityType,
        activityName: data.activityName || '',
        activityDesc: data.activityDesc || '',
        backgroundFileUrl: data.backgroundFileUrl || '',
        backgroundFileId: data.backgroundFileId || '',
        buttonFileUrl: data.buttonFileUrl || '',
        buttonFileId: data.buttonFileId || '',
        bonusCoins:
          data.bonusCoins === 0 || data.bonusCoins ? data.bonusCoins : null,
        startTime: data.startTime || '',
        endTime: data.endTime || '',
        activityTimeRange:
          data.startTime && data.endTime ? [data.startTime, data.endTime] : [],
        moduleConfigs:
          activityType === ACTIVITY_TYPE_SCORE
            ? []
            : modules.length
              ? modules
              : [createModule()]
      }
    },
    normalizeModule(item = {}) {
      return {
        ...createModule(),
        moduleName: item.moduleName || '',
        contentType:
          item.contentType === 1 || item.contentType === 2
            ? item.contentType
            : '',
        iosGoodsIds: Array.isArray(item.iosGoodsIds) ? item.iosGoodsIds : [],
        androidGoodsIds: Array.isArray(item.androidGoodsIds)
          ? item.androidGoodsIds
          : [],
        recommendType: Number(item.recommendType || 1),
        recommendCount: Number(item.recommendCount || 10),
        parentPlayIds: Array.isArray(item.parentPlayIds) ? item.parentPlayIds : [],
        parentPlayNames: Array.isArray(item.parentPlayNames) ? item.parentPlayNames : [],
        languageCards: this.normalizeLanguageCards(item.recommendPlayContent)
      }
    },
    normalizeLanguageCards(recommendPlayContent = []) {
      return (recommendPlayContent || []).map((item) => ({
        ...createLanguageCard(),
        langId: item.langId || '',
        langName: item.langName || '',
        dramas:
          Array.isArray(item.parentPlayIds) && item.parentPlayIds.length
            ? item.parentPlayIds.map((play) => ({
                ...createDramaItem(),
                id:
                  play.parentPlayId === 0 || play.parentPlayId
                    ? play.parentPlayId
                    : '',
                name: play.parentPlayName || '',
                createTime: play.createTime || ''
              }))
            : [createDramaItem()]
      }))
    },
    mergeDramaOptions(existing = [], incoming = []) {
      const optionMap = new Map()
      ;[...existing, ...incoming].forEach((item) => {
        if (!item) return
        const id = item.id === 0 || item.id ? item.id : item.parentPlayId
        if (id === '' || id === undefined || id === null) return
        const current = optionMap.get(String(id))
        const nextName =
          item.name || item.parentPlayName || (current && current.name) || ''
        optionMap.set(String(id), {
          id,
          name: nextName
        })
      })
      return Array.from(optionMap.values())
    },
    async loadSpecifiedDramaNames() {
      const tasks = this.formData.moduleConfigs
        .filter(
          (module) =>
            Number(module.contentType) === 2 &&
            Number(module.recommendType) === 2 &&
            module.parentPlayId &&
            !module.parentPlayName
        )
        .map(async (module) => {
          try {
            const { data } = await getPrimaryInfo(module.parentPlayId)
            const option = {
              id: data.id,
              name: data.playName
            }
            module.parentPlayName = option.name
            this.specifiedDramaOptions = this.mergeDramaOptions(
              this.specifiedDramaOptions,
              [option]
            )
          } catch (error) {}
        })
      await Promise.all(tasks)
    },
    loadLanguageCardOptions() {
      this.formData.moduleConfigs.forEach((module, moduleIndex) => {
        ;(module.languageCards || []).forEach((card, cardIndex) => {
          if (card.langId) {
            this.searchLanguageDramas(moduleIndex, cardIndex)
          }
        })
      })
    },
    addLanguageCard(moduleIndex) {
      if (this.readonly) return
      this.formData.moduleConfigs[moduleIndex].languageCards.push(
        createLanguageCard()
      )
    },
    removeLanguageCard(moduleIndex, cardIndex) {
      if (this.readonly) return
      this.formData.moduleConfigs[moduleIndex].languageCards.splice(cardIndex, 1)
    },
    isLanguageDisabled(cards, langId, currentIndex) {
      return cards.some(
        (item, index) =>
          index !== currentIndex && String(item.langId) === String(langId)
      )
    },
    handleLanguageChange(moduleIndex, cardIndex, langId) {
      const module = this.formData.moduleConfigs[moduleIndex]
      const language =
        this.languageList.find((item) => String(item.id) === String(langId)) ||
        {}
      this.$set(module.languageCards, cardIndex, {
        ...createLanguageCard(),
        langId,
        langName: language.name || ''
      })
      this.searchLanguageDramas(moduleIndex, cardIndex)
    },
    searchLanguageDramas: debounce(async function (moduleIndex, cardIndex, query = '') {
      const module = this.formData.moduleConfigs[moduleIndex]
      if (!module) return
      const card = module.languageCards[cardIndex]
      if (!card || !card.langId) return
      this.$set(card, 'loading', true)
      try {
        const { data } = await getPrimaryList({
          current: 1,
          size: 100,
          originalLangId: card.langId,
          keyword: query
        })
        const options = (data.records || []).map((item) => ({
          id: item.id,
          name: item.playName
        }))
        this.$set(
          card,
          'dramaOptions',
          this.mergeDramaOptions(card.dramaOptions || [], [
            ...options,
            ...(card.dramas || [])
          ])
        )
      } catch (error) {
        this.$Message.error('搜索语言剧集失败')
      } finally {
        this.$set(card, 'loading', false)
      }
    }, 300),
    getCardDramaOptions(card) {
      return this.mergeDramaOptions(card.dramaOptions || [], card.dramas || [])
    },
    isCardDramaDisabled(card, dramaId, currentIndex) {
      return (card.dramas || []).some(
        (item, index) =>
          index !== currentIndex &&
          item.id !== '' &&
          String(item.id) === String(dramaId)
      )
    },
    handleCardDramaChange(moduleIndex, cardIndex, dramaIndex, dramaId) {
      const card =
        this.formData.moduleConfigs[moduleIndex].languageCards[cardIndex]
      if (!card) return
      if (dramaId === '' || dramaId === undefined || dramaId === null) {
        this.$set(card.dramas, dramaIndex, createDramaItem())
        return
      }
      const option = this.getCardDramaOptions(card).find(
        (item) => String(item.id) === String(dramaId)
      )
      this.$set(card.dramas, dramaIndex, {
        ...card.dramas[dramaIndex],
        id: dramaId,
        name: option ? option.name : '',
        createTime: ''
      })
    },
    addDramaToCard(moduleIndex, cardIndex) {
      if (this.readonly) return
      this.formData.moduleConfigs[moduleIndex].languageCards[cardIndex].dramas.push(
        createDramaItem()
      )
    },
    removeDramaFromCard(moduleIndex, cardIndex, dramaIndex) {
      if (this.readonly) return
      const dramas =
        this.formData.moduleConfigs[moduleIndex].languageCards[cardIndex].dramas
      if (dramas.length <= 1) {
        this.$Message.warning('至少保留一项剧目')
        return
      }
      dramas.splice(dramaIndex, 1)
    },
    searchSpecifiedDramas: debounce(async function (query = '') {
      this.specifiedDramaLoading = true
      try {
        const { data } = await getPrimaryList({
          current: 1,
          size: 100,
          keyword: query
        })
        const options = (data.records || []).map((item) => ({
          id: item.id,
          name: item.playName
        }))
        this.specifiedDramaOptions = this.mergeDramaOptions(
          this.specifiedDramaOptions,
          options
        )
      } catch (error) {
        this.$Message.error('搜索指定剧集失败')
      } finally {
        this.specifiedDramaLoading = false
      }
    }, 300),
    handleSpecifiedDramaChange(moduleIndex, dramaIndex, dramaId) {
      const module = this.formData.moduleConfigs[moduleIndex]
      if (!module) return
      if (!module.parentPlayIds) {
        this.$set(module, 'parentPlayIds', [])
      }
      if (!module.parentPlayNames) {
        this.$set(module, 'parentPlayNames', [])
      }
      if (dramaId === '' || dramaId === undefined || dramaId === null) {
        module.parentPlayIds[dramaIndex] = ''
        module.parentPlayNames[dramaIndex] = ''
        return
      }
      const option = this.specifiedDramaOptions.find(
        (item) => String(item.id) === String(dramaId)
      )
      module.parentPlayIds[dramaIndex] = dramaId
      module.parentPlayNames[dramaIndex] = option ? option.name : ''
    },
    getSpecifiedDramaOptions(module, dramaIndex) {
      return this.mergeDramaOptions(this.specifiedDramaOptions, [
        {
          id: module.parentPlayIds && module.parentPlayIds[dramaIndex],
          name: module.parentPlayNames && module.parentPlayNames[dramaIndex]
        }
      ])
    },
    isDramaSelected(module, currentIndex, dramaId) {
      if (!module.parentPlayIds || !dramaId) return false
      // 检查除当前索引外的其他位置是否已选中该剧集
      return module.parentPlayIds.some(
        (id, index) => index !== currentIndex && String(id) === String(dramaId)
      )
    },
    validateModules() {
      if (!this.requiresModules) return true
      if (!this.formData.moduleConfigs.length) {
        this.$Message.error('请至少配置一个活动模块')
        return false
      }
      for (let index = 0; index < this.formData.moduleConfigs.length; index++) {
        const module = this.formData.moduleConfigs[index]
        const prefix = `模块${index + 1}`
        if (!module.moduleName) {
          this.$Message.error(`请输入${prefix}标题`)
          return false
        }
        if (!module.contentType) {
          this.$Message.error(`请选择${prefix}内容类型`)
          return false
        }
        if (Number(module.contentType) === 1) {
          if (!Array.isArray(module.iosGoodsIds) || !module.iosGoodsIds.length) {
            this.$Message.error(`请选择${prefix}的iOS付费项`)
            return false
          }
          if (
            !Array.isArray(module.androidGoodsIds) ||
            !module.androidGoodsIds.length
          ) {
            this.$Message.error(`请选择${prefix}的安卓付费项`)
            return false
          }
        }
        if (Number(module.contentType) === 2) {
          if (!module.recommendCount || Number(module.recommendCount) < 1) {
            this.$Message.error(`请输入${prefix}的剧集数量`)
            return false
          }
          if (Number(module.recommendType) === 2 && !module.parentPlayId) {
            this.$Message.error(`请选择${prefix}的指定剧集`)
            return false
          }
          if (Number(module.recommendType) === 1) {
            for (let cardIndex = 0; cardIndex < module.languageCards.length; cardIndex++) {
              const card = module.languageCards[cardIndex]
              if (!card.langId) {
                this.$Message.error(`请选择${prefix}语言卡片的语言`)
                return false
              }
              const validDramaCount = (card.dramas || []).filter(
                (item) => item.id
              ).length
              if (!validDramaCount) {
                this.$Message.error(`请补充${prefix}语言卡片的剧目`)
                return false
              }
            }
          }
        }
      }
      return true
    },
    buildRecommendPlayContentPayload(cards = []) {
      return cards
        .filter((card) => card.langId)
        .map((card) => ({
          langId: card.langId,
          parentPlayIds: (card.dramas || [])
            .filter((item) => item.id)
            .map((item) => ({
              parentPlayId: item.id,
              createTime: item.createTime || dayjs().format('YYYY-MM-DD HH:mm:ss')
            }))
        }))
        .filter((item) => item.parentPlayIds.length)
    },
    buildModulePayload() {
      return this.formData.moduleConfigs.map((module) => {
        const payload = {
          moduleName: module.moduleName.trim(),
          contentType: Number(module.contentType),
          iosGoodsIds: [],
          androidGoodsIds: [],
          recommendType: null,
          recommendCount: null,
          parentPlayId: null,
          recommendPlayContent: []
        }
        if (payload.contentType === 1) {
          payload.iosGoodsIds = [...module.iosGoodsIds]
          payload.androidGoodsIds = [...module.androidGoodsIds]
        }
        if (payload.contentType === 2) {
          payload.recommendType = Number(module.recommendType || 1)
          payload.recommendCount = Number(module.recommendCount || 1)
          if (payload.recommendType === 1) {
            payload.recommendPlayContent = this.buildRecommendPlayContentPayload(
              module.languageCards
            )
          } else {
            payload.parentPlayId = module.parentPlayId || null
          }
        }
        return payload
      })
    },
    buildSubmitPayload() {
      const payload = {
        activityType: this.formData.activityType,
        activityName: this.formData.activityName.trim(),
        activityDesc: this.showsActivityDesc
          ? this.formData.activityDesc.trim()
          : null,
        backgroundFileId: this.formData.backgroundFileId,
        buttonFileId: this.isScoreActivity ? this.formData.buttonFileId : null,
        bonusCoins:
          this.formData.bonusCoins === 0 || this.formData.bonusCoins
            ? this.formData.bonusCoins
            : null,
        startTime: this.needsHolidayTime ? this.formData.startTime : null,
        endTime: this.needsHolidayTime ? this.formData.endTime : null,
        moduleConfigs: this.requiresModules ? this.buildModulePayload() : []
      }
      if (this.formData.id) {
        payload.id = this.formData.id
      }
      return payload
    },
    handleSubmit() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        if (this.needsHolidayTime && this.formData.activityTimeRange.length !== 2) {
          this.$Message.error('请选择活动时间')
          return
        }
        if (!this.validateModules()) return
        this.loading = true
        try {
          const payload = this.buildSubmitPayload()
          if (this.formData.id) {
            await editActivityConfig(payload)
          } else {
            await addActivityConfig(payload)
          }
          this.$Message.success('保存成功')
          this.visible = false
          this.$emit('on-success')
        } catch (error) {
          this.$Message.error('保存失败')
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped lang="less">
.module-toolbar {
  display: flex;
  margin: 4px 0 16px;
}

.module-toolbar--bottom {
  justify-content: flex-start;
  padding-left: 110px;
}

.module-card {
  background: #f7f9fc;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 16px;
  z-index: 1000;
}

.module-card__form-item {
  margin-bottom: 14px;
}

.module-card__form-item:last-child {
  margin-bottom: 0;
}

.module-card__inline {
  display: flex;
  align-items: center;
  gap: 10px;
}

.module-card__field {
  flex: 1;
  min-width: 0;
}

.module-card__remove {
  flex: none;
  font-size: 22px;
  color: #515a6e;
  cursor: pointer;
}

.module-card__toggle {
  flex: none;
  display: inline-flex;
  align-items: center;
  color: #2d8cf0;
  cursor: pointer;
  white-space: nowrap;
}

.module-card__arrow {
  margin-left: 4px;
  transition: transform 0.2s ease;
}

.module-card__arrow--expanded {
  transform: rotate(180deg);
}

.module-card__body {
  padding-top: 2px;
}

.module-recommend-manual {
  margin-left: 110px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.module-recommend-manual__button {
  order: 1;
  margin-bottom: 8px;
}

.module-hint {
  order: 2;
  margin: 0 0 8px;
  color: #808695;
  line-height: 1.6;
  max-width: 420px;
}

.language-card {
  // position: relative;
  margin-top: 12px;
  margin-left: 110px;
  padding: 14px 42px 10px 14px;
  background: #edf4ff;
  border-radius: 6px;
  z-index: 0;
}

.language-card__remove {
  // position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  padding: 4px;
  line-height: 1;
  font-size: 18px;
  cursor: pointer;
  color: #808695;
  pointer-events: auto;
  margin-left: auto;
  align-self: flex-end;
}

.language-card__append {
  display: inline-block;
  margin-left: 80px;
  color: #2d8cf0;
  cursor: pointer;
}

.drama-row {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 10;
}

.drama-row__remove {
  margin-left: 8px;
  color: #ed4014;
  font-size: 18px;
  cursor: pointer;
}

.drama-select {
  position: relative;
  z-index: 20;
}

:deep(.ivu-select-dropdown) {
  z-index: 9999 !important;
  position: absolute !important;
}

.score-preview {
  position: relative;
  width: 220px;
  min-height: 260px;
  padding: 12px 12px 64px;
  border: 1px solid #dcdee2;
  border-radius: 8px;
  background: #f8f8f9;
  overflow: hidden;
}

.score-preview--clickable {
  cursor: pointer;
}

.score-preview__bg {
  width: 100%;
  display: block;
  border-radius: 6px;
}

.score-preview__placeholder {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbbec4;
}

.score-preview__button {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 12px;
}

.score-preview__button img {
  width: 100%;
  display: block;
}
</style>
